import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Minus, Calendar } from 'lucide-react';
import { clinicianService } from '../services/clinicianService';

const ClinicianCalendarPage = () => {
  const navigate = useNavigate();
  
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Clinician Calendar';
  }, []);

  // State for data
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [expandedAppointments, setExpandedAppointments] = useState(new Set());

  // State for clinician info
  const [clinicianId, setClinicianId] = useState(null);

  // Fetch clinician info and initial data on component mount
  useEffect(() => {
    fetchClinicianInfo();
  }, []);

  // Fetch appointments when selected date changes
  useEffect(() => {
    if (clinicianId && selectedDate) {
      fetchAppointmentsForDate(selectedDate);
    }
  }, [clinicianId, selectedDate]);

  const fetchClinicianInfo = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('👤 Clinician Calendar: Current user from localStorage:', user);
      
      if (user.id) {
        const clinician = await clinicianService.getClinicianByUserId(user.id);
        console.log('👩‍⚕️ Clinician Calendar: Clinician info retrieved:', clinician);
        setClinicianId(clinician.id);
        console.log('🆔 Clinician Calendar: Set clinician ID to:', clinician.id);
      } else {
        console.log('❌ Clinician Calendar: No user ID found in localStorage');
      }
    } catch (error) {
      console.error('❌ Clinician Calendar: Error fetching clinician info:', error);
      // Handle authentication errors
      if (error.message.includes('Could not validate credentials') || error.message.includes('401')) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/auth/login';
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointmentsForDate = async (date) => {
    try {
      setLoading(true);
      // Format date as YYYY-MM-DD without timezone conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      console.log('🔍 Clinician Calendar: Fetching appointments for clinician:', clinicianId, 'date:', dateString, 'original date:', date);
      
      // Get real appointments for the specific date (like doctor's module)
      let appointmentsData = await clinicianService.getClinicianBookings(clinicianId, dateString);
      console.log('📅 Clinician Calendar: Raw appointments data from API:', appointmentsData);
      console.log('📊 Clinician Calendar: Number of real appointments:', appointmentsData.length);
      
      // Only display real appointments from the database
      console.log('📋 Clinician Calendar: Total real appointments to display:', appointmentsData.length);
      
      // Sort all appointments by time
      appointmentsData.sort((a, b) => {
        const timeA = a.appointment_time || a.time; // Handle both API format (appointment_time) and dummy format (time)
        const timeB = b.appointment_time || b.time;
        return timeA.localeCompare(timeB);
      });
      console.log('📅 Clinician Calendar: Appointments sorted by time:', appointmentsData.map(apt => ({ 
        time: apt.appointment_time || apt.time, 
        name: apt.patient_name || apt.patientName || 'Unknown Patient',
        type: apt.appointment_type,
        reason: apt.reason
      })));
      
      setAppointments(appointmentsData);
    } catch (error) {
      console.error('❌ Clinician Calendar: Error fetching appointments for date:', error);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  // Removed dummy appointments generator - only real data from API will be displayed

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Reset time to compare only dates
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);
    
    if (compareDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (compareDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else if (compareDate.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
    }
  };

  const handleDateChange = (direction) => {
    setSelectedDate(prevDate => {
      const newDate = new Date(prevDate);
      if (direction === 'next') {
        newDate.setDate(newDate.getDate() + 1);
      } else {
        newDate.setDate(newDate.getDate() - 1);
      }
      return newDate;
    });
    // Clear expanded appointments when date changes
    setExpandedAppointments(new Set());
  };

  const handleMonthChange = (direction) => {
    setSelectedMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      if (direction === 'next') {
        newMonth.setMonth(newMonth.getMonth() + 1);
      } else {
        newMonth.setMonth(newMonth.getMonth() - 1);
      }
      return newMonth;
    });
  };

  const handleAppointmentToggle = (appointmentId) => {
    setExpandedAppointments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(appointmentId)) {
        newSet.delete(appointmentId);
      } else {
        newSet.add(appointmentId);
      }
      return newSet;
    });
  };

  // Generate calendar days (weekdays only)
  const generateCalendarDays = () => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    const currentDate = new Date(firstDay);
    
    // Find the first Monday of or before the first day of the month
    while (currentDate.getDay() !== 1) { // 1 = Monday
      currentDate.setDate(currentDate.getDate() - 1);
    }
    
    // Generate 5 weeks of weekdays only (5 columns × 5 rows = 25 weekdays)
    for (let week = 0; week < 5; week++) {
      for (let day = 0; day < 5; day++) { // Monday to Friday only
        days.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      // Skip weekend (Saturday and Sunday)
      currentDate.setDate(currentDate.getDate() + 2);
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-600">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Top Banner */}
      <div className="bg-blue-600 text-white px-6 py-1.5 rounded-lg mb-6">
        <h1 className="text-lg font-semibold text-center">Calendar</h1>
      </div>

      <div className="flex gap-6">
        {/* Left Column - Monthly Calendar (35%) */}
        <div className="w-[35%]">
          <div className="bg-white rounded-lg shadow-md">
            {/* Calendar Header */}
            <div className="bg-blue-500 text-white px-3 py-3 rounded-t-lg flex justify-center items-center">
              <div className="flex items-center space-x-3">
                <button 
                  onClick={() => handleMonthChange('prev')}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <h3 className="font-semibold text-sm">{selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
                <button 
                  onClick={() => handleMonthChange('next')}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Days of Week Header */}
            <div className="grid grid-cols-5 gap-px bg-blue-200">
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((day) => (
                <div key={day} className="bg-blue-100 px-2 py-0.5 text-center">
                  <span className="text-xs font-medium text-blue-700">{day}</span>
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-5 gap-px bg-blue-200">
              {calendarDays.map((date, index) => {
                const isCurrentMonth = date.getMonth() === selectedMonth.getMonth();
                const isToday = date.toDateString() === new Date().toDateString();
                const isSelectedDate = date.toDateString() === selectedDate.toDateString();
                
                return (
                  <div 
                    key={index} 
                    className={`px-2 py-2 text-center border cursor-pointer transition-colors ${
                      isToday 
                        ? 'bg-green-300 border-green-500 font-bold text-green-900' 
                        : isSelectedDate 
                          ? 'bg-blue-100 border-blue-400 font-bold text-blue-800' 
                          : 'bg-white border-blue-200 hover:bg-blue-50'
                    }`}
                    onClick={() => {
                      setSelectedDate(date);
                      setExpandedAppointments(new Set()); // Clear expanded state when date changes
                    }}
                  >
                    <span className={`text-xs font-medium ${
                      isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                    }`}>
                      {date.getDate()}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Column - Daily Appointments (65%) */}
        <div className="w-[65%]">
          <div className="bg-white rounded-lg shadow-md h-[464px] flex flex-col">
            {/* Appointment Header */}
            <div className="bg-blue-600 text-white px-3 py-2 rounded-t-lg flex justify-center items-center">
              <div className="flex items-center space-x-3">
                <button 
                  onClick={() => handleDateChange('prev')}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-1 transition-all duration-200 hover:scale-110"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <h3 className="font-semibold text-sm">{formatDate(selectedDate)}</h3>
                <button 
                  onClick={() => handleDateChange('next')}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-1 transition-all duration-200 hover:scale-110"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Fixed Column Headers */}
            <div className="px-3 py-3 bg-gray-100 border-b-2 border-gray-300 shadow-sm">
              <div className="grid grid-cols-2 gap-6">
                <div className="flex">
                  <div className="w-[5%]"></div>
                  <div className="w-[25%] text-center">
                    <span className="text-sm font-medium text-gray-700">Time</span>
                  </div>
                  <div className="w-[65%] text-center">
                    <span className="text-sm font-medium text-gray-700">Patient Name</span>
                  </div>
                  <div className="w-[5%]"></div>
                </div>
                <div className="flex">
                  <div className="w-[5%]"></div>
                  <div className="w-[25%] text-center">
                    <span className="text-sm font-medium text-gray-700">Time</span>
                  </div>
                  <div className="w-[65%] text-center">
                    <span className="text-sm font-medium text-gray-700">Patient Name</span>
                  </div>
                  <div className="w-[5%]"></div>
                </div>
              </div>
            </div>

            {/* Scrollable Appointments Content */}
            <div className="flex-1 p-3 overflow-y-auto custom-scrollbar"
                 style={{
                   scrollbarWidth: 'thin',
                   scrollbarColor: '#60a5fa #e2e8f0'
                 }}>
             
             {/* Appointments in two independent columns (sorted by time) */}
             {appointments.length === 0 ? (
               <div className="flex items-center justify-center h-64">
                 <div className="text-center">
                   <Calendar className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                   <p className="text-gray-500 text-lg font-medium">No appointments scheduled</p>
                   <p className="text-gray-400 text-sm mt-2">There are no bookings for {formatDate(selectedDate)}</p>
                 </div>
               </div>
             ) : (
               <div className="flex gap-4">
                 {/* Left Column */}
                 <div className="w-1/2 pr-3">
                   <div className="space-y-3">
                     {appointments.filter((_, index) => index % 2 === 0).map((appointment) => {
                     const isExpanded = expandedAppointments.has(appointment.id);
                     return (
                       <div key={appointment.id}>
                         {/* Main appointment row */}
                         <div className="flex items-center py-1 px-3 border-l-4 border-l-blue-400 border-b-2 border-b-gray-200 last:border-b-0 hover:bg-gradient-to-r hover:from-blue-50 hover:to-transparent hover:border-l-blue-600 transition-all duration-300 rounded-lg shadow-sm hover:shadow-md">
                           <div className="w-[5%]"></div>
                           <div className="w-[25%] text-left">
                             <span className="text-sm font-medium text-gray-800">{formatTime(appointment.appointment_time || appointment.time)}</span>
                           </div>
                           <div className="w-[65%] flex items-center justify-between">
                             <div className="flex items-center space-x-2">
                               <span className="text-sm text-gray-700">{appointment.patient_name || appointment.patientName}</span>
                             </div>
                             <button 
                               onClick={() => handleAppointmentToggle(appointment.id)}
                               className="text-blue-600 hover:text-blue-800 p-1 transition-colors"
                             >
                               {isExpanded ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                             </button>
                           </div>
                           <div className="w-[5%]"></div>
                         </div>
                         
                         {/* Expanded details */}
                        {isExpanded && (
                          <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r mb-2">
                            <div className="space-y-2 text-sm">
                              {/* Line 1: Patient ID and Patient Name */}
                              <div className="flex justify-between items-center">
                                                                  <span className="font-medium text-gray-700">Patient ID: <span className="font-normal">{appointment.patient_string_id || appointment.patient_id || 'N/A'}</span></span>
                                <span className="font-medium text-gray-700">Patient Name: <span className="font-normal">{appointment.patient_name || appointment.patientName}</span></span>
                              </div>
                              
                              {/* Line 2: Patient Phone and Patient Email */}
                              <div className="flex justify-between items-center">
                                <span className="font-medium text-gray-700">Phone: <span className="font-normal">{appointment.patient_phone || 'N/A'}</span></span>
                                <span className="font-medium text-gray-700">Email: <span className="font-normal">{appointment.patient_email || 'N/A'}</span></span>
                              </div>
                              
                              {/* Line 3: 4 Action Buttons */}
                              <div className="flex justify-between items-center mt-3 pt-2 border-t border-blue-200">
                                <button className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors">
                                  Details
                                </button>
                                                                  <button 
                                    onClick={() => {
                                      console.log('📂 Patient File clicked for appointment:', appointment);
                                      const patientData = {
                                        patient_id: appointment.patient_string_id || appointment.patient_id,
                                        patient_name: appointment.patient_name || `${appointment.patient_first_name || ''} ${appointment.patient_last_name || ''}`.trim(),
                                        appointment_id: appointment.id,
                                        appointment_date: appointment.appointment_date,
                                        appointment_time: appointment.appointment_time
                                      };
                                      // Use string patient ID for navigation (like "P000002")
                                      const navigationPatientId = appointment.patient_string_id || appointment.patient_id;
                                      navigate(`/clinician/patientfiles/${navigationPatientId}/general_details`, { 
                                        state: { patientData } 
                                      });
                                    }}
                                    className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors"
                                  >
                                    Patient File
                                  </button>
                                <button className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors">
                                  Edit Booking
                                </button>
                                <button 
                                  onClick={() => {
                                    // Navigate to clinical telehealth notes only (no general telehealth for clinicians)
                                    console.log('🏥 Navigating to Clinical Telehealth with data:', appointment);
                                    
                                    const appointmentData = {
                                      id: appointment.id,
                                      patient_id: appointment.patient_string_id || appointment.patient_id,  // Use string ID for patient lookup
                                      patient_name: appointment.patient_name || appointment.patientName,
                                      doctor_name: appointment.doctor_name || appointment.doctor,  // Include doctor name
                                      clinician_name: appointment.clinician_name || appointment.clinician,
                                      appointment_type: 'Clinical Telehealth',
                                      appointment_date: appointment.appointment_date,
                                      appointment_time: appointment.appointment_time,
                                      reason: appointment.reason,
                                      specialty: appointment.specialty,
                                      clinic_location: appointment.clinic_location,
                                      schedule_type: appointment.schedule_type,
                                      status: appointment.status
                                    };
                                    
                                    navigate(`/clinician/clinicaltelenotes/${appointment.id}`, { state: { appointmentData } });
                                  }}
                                  className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors"
                                >
                                  Go to Booking
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                       </div>
                     );
                   })}
                 </div>
               </div>
               
               {/* Right Column */}
               <div className="w-1/2 pl-3">
                 <div className="space-y-3">
                   {appointments.filter((_, index) => index % 2 === 1).map((appointment) => {
                     const isExpanded = expandedAppointments.has(appointment.id);
                     return (
                       <div key={appointment.id}>
                         {/* Main appointment row */}
                         <div className="flex items-center py-1 px-3 border-l-4 border-l-green-400 border-b-2 border-b-gray-200 last:border-b-0 hover:bg-gradient-to-r hover:from-green-50 hover:to-transparent hover:border-l-green-600 transition-all duration-300 rounded-lg shadow-sm hover:shadow-md">
                           <div className="w-[5%]"></div>
                           <div className="w-[25%] text-center">
                             <span className="text-sm font-medium text-gray-800">{formatTime(appointment.appointment_time || appointment.time)}</span>
                           </div>
                           <div className="w-[65%] flex items-center justify-between">
                             <div className="flex items-center space-x-2">
                               <span className="text-sm text-gray-700">{appointment.patient_name || appointment.patientName}</span>
                             </div>
                             <button 
                               onClick={() => handleAppointmentToggle(appointment.id)}
                               className="text-blue-600 hover:text-blue-800 p-1 transition-colors"
                             >
                               {isExpanded ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                             </button>
                           </div>
                           <div className="w-[5%]"></div>
                         </div>
                         
                         {/* Expanded details */}
                        {isExpanded && (
                          <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-r mb-2">
                            <div className="space-y-2 text-sm">
                              {/* Line 1: Patient ID and Patient Name */}
                              <div className="flex justify-between items-center">
                                                                  <span className="font-medium text-gray-700">Patient ID: <span className="font-normal">{appointment.patient_string_id || appointment.patient_id || 'N/A'}</span></span>
                                <span className="font-medium text-gray-700">Patient Name: <span className="font-normal">{appointment.patient_name || appointment.patientName}</span></span>
                              </div>
                              
                              {/* Line 2: Patient Phone and Patient Email */}
                              <div className="flex justify-between items-center">
                                <span className="font-medium text-gray-700">Phone: <span className="font-normal">{appointment.patient_phone || 'N/A'}</span></span>
                                <span className="font-medium text-gray-700">Email: <span className="font-normal">{appointment.patient_email || 'N/A'}</span></span>
                              </div>
                              
                              {/* Line 3: 4 Action Buttons */}
                              <div className="flex justify-between items-center mt-3 pt-2 border-t border-green-200">
                                <button className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors">
                                  Details
                                </button>
                                                                  <button 
                                    onClick={() => {
                                      console.log('📂 Patient File clicked for appointment:', appointment);
                                      const patientData = {
                                        patient_id: appointment.patient_string_id || appointment.patient_id,
                                        patient_name: appointment.patient_name || `${appointment.patient_first_name || ''} ${appointment.patient_last_name || ''}`.trim(),
                                        appointment_id: appointment.id,
                                        appointment_date: appointment.appointment_date,
                                        appointment_time: appointment.appointment_time
                                      };
                                      // Use string patient ID for navigation (like "P000002")
                                      const navigationPatientId = appointment.patient_string_id || appointment.patient_id;
                                      navigate(`/clinician/patientfiles/${navigationPatientId}/general_details`, { 
                                        state: { patientData } 
                                      });
                                    }}
                                    className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors"
                                  >
                                    Patient File
                                  </button>
                                <button className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors">
                                  Edit Booking
                                </button>
                                <button 
                                  onClick={() => {
                                    // Navigate to clinical telehealth notes only (no general telehealth for clinicians)
                                    console.log('🏥 Navigating to Clinical Telehealth with data:', appointment);
                                    
                                    const appointmentData = {
                                      id: appointment.id,
                                      patient_id: appointment.patient_string_id || appointment.patient_id,  // Use string ID for patient lookup
                                      patient_name: appointment.patient_name || appointment.patientName,
                                      doctor_name: appointment.doctor_name || appointment.doctor,  // Include doctor name
                                      clinician_name: appointment.clinician_name || appointment.clinician,
                                      appointment_type: 'Clinical Telehealth',
                                      appointment_date: appointment.appointment_date,
                                      appointment_time: appointment.appointment_time,
                                      reason: appointment.reason,
                                      specialty: appointment.specialty,
                                      clinic_location: appointment.clinic_location,
                                      schedule_type: appointment.schedule_type,
                                      status: appointment.status
                                    };
                                    
                                    navigate(`/clinician/clinicaltelenotes/${appointment.id}`, { state: { appointmentData } });
                                  }}
                                  className="bg-white border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors"
                                >
                                  Go to Booking
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                       </div>
                     );
                   })}
                 </div>
               </div>
               </div>
             )}
                             
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicianCalendarPage;